import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { useAuth } from './useAuth';
import { useTenant } from './useTenant';
import { apiClient } from '@/api';
import type { UserToken, CreateTokenRequest, CreateTokenResponse } from '@/types';

interface TokensResponse {
  tokens: UserToken[];
  total: number;
}

interface UseTokensOptions {
  limit?: number;
  offset?: number;
}

interface RevealTokenResponse {
  token: string;
}

interface UseTokensResult {
  tokens: UserToken[];
  total: number;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  createToken: (request: CreateTokenRequest) => Promise<CreateTokenResponse>;
  revokeToken: (tokenId: string) => Promise<void>;
  bulkRevokeTokens: (tokenIds: string[]) => Promise<void>;
  revealToken: (tokenId: string) => Promise<string>;
  isCreating: boolean;
  isRevoking: boolean;
}

export function useTokens(options: UseTokensOptions = {}): UseTokensResult {
  const { session } = useAuth();
  const { tenant } = useTenant();
  const { limit = 50, offset = 0 } = options;
  const [isCreating, setIsCreating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const queryParams = new URLSearchParams();
  if (limit) queryParams.set('limit', String(limit));
  if (offset) queryParams.set('offset', String(offset));
  if (tenant?.id) queryParams.set('tenant_id', tenant.id);

  const endpoint = `/api/v1/tokens?${queryParams.toString()}`;

  const fetcher = async (url: string): Promise<TokensResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<TokensResponse>(url, { token: session.access_token });
  };

  const { data, error, isLoading, mutate } = useSWR<TokensResponse>(
    session?.access_token ? endpoint : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    }
  );

  const refresh = async () => {
    await mutate();
  };

  const createToken = useCallback(
    async (request: CreateTokenRequest): Promise<CreateTokenResponse> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsCreating(true);
      try {
        const params = tenant?.id ? `?tenant_id=${tenant.id}` : '';
        const response = await apiClient.post<CreateTokenResponse>(
          `/api/v1/tokens${params}`,
          request,
          { token: session.access_token }
        );
        await mutate();
        return response;
      } finally {
        setIsCreating(false);
      }
    },
    [session?.access_token, tenant?.id, mutate]
  );

  const revokeToken = useCallback(
    async (tokenId: string): Promise<void> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsRevoking(true);
      try {
        const params = tenant?.id ? `?tenant_id=${tenant.id}` : '';
        await apiClient.delete(`/api/v1/tokens/${tokenId}${params}`, {
          token: session.access_token,
        });
        await mutate();
      } finally {
        setIsRevoking(false);
      }
    },
    [session?.access_token, tenant?.id, mutate]
  );

  const bulkRevokeTokens = useCallback(
    async (tokenIds: string[]): Promise<void> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsRevoking(true);
      try {
        const params = tenant?.id ? `?tenant_id=${tenant.id}` : '';
        // Revoke tokens sequentially to avoid overwhelming the API
        for (const tokenId of tokenIds) {
          await apiClient.delete(`/api/v1/tokens/${tokenId}${params}`, {
            token: session.access_token,
          });
        }
        await mutate();
      } finally {
        setIsRevoking(false);
      }
    },
    [session?.access_token, tenant?.id, mutate]
  );

  const revealToken = useCallback(
    async (tokenId: string): Promise<string> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      const params = tenant?.id ? `?tenant_id=${tenant.id}` : '';
      const response = await apiClient.get<RevealTokenResponse>(
        `/api/v1/tokens/${tokenId}/reveal${params}`,
        { token: session.access_token }
      );
      return response.token;
    },
    [session?.access_token, tenant?.id]
  );

  return {
    tokens: data?.tokens ?? [],
    total: data?.total ?? 0,
    isLoading,
    error: error ?? null,
    refresh,
    createToken,
    revokeToken,
    bulkRevokeTokens,
    revealToken,
    isCreating,
    isRevoking,
  };
}
